import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import type { ShadowSettings } from '@common/types/shadow-settings.type';
import { isEmptyButtonLabel } from '@common/helpers/forms';
import { WebBaseControlTypedSettingsComponent } from '@web/widgets/common/abstract/web-base-control-typed-settings.component';
import {
    type ButtonSettings,
    ButtonActionType,
    type ProductButtonFormGroup,
    type ProductButtonFormType,
} from '@web/widgets/product-card/type';
import { EButtonStyleType, type ESize } from '@common/enums';
import { ButtonMode, type CustomButtonEventPayload } from '@components/sp-custom-button';
import { ERadioGroupType } from '@ui/sp-radio-group';
import { ThemeStateService } from '@sites/site-theme/src/state';
import { TranslateModule } from '@ngx-translate/core';
import { SpCustomButtonModule } from '@components/sp-custom-button/sp-custom-button.module';
import { SpRadioGroupComponent } from '@ui/sp-radio-group';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { WebSettingsModule } from '@web/shared/components/web-settings/web-settings.module';
import { SpShadowSettingsComponent } from '@components/sp-shadow-settings/sp-shadow-settings.component';
import { SpSwitchComponent } from '@ui/sp-switch';

@Component({
    selector: 'web-button-settings',
    templateUrl: './button-settings.component.html',
    styleUrls: ['./button-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        WebSettingsModule,
        FormsModule,
        ReactiveFormsModule,
        SpRadioGroupComponent,
        SpCustomButtonModule,
        TranslateModule,
        SpShadowSettingsComponent,
        SpSwitchComponent,
    ],
})
export class ButtonSettingsComponent extends WebBaseControlTypedSettingsComponent<ProductButtonFormGroup> {
    private readonly themeState = inject(ThemeStateService);

    protected readonly EButtonStyleType = EButtonStyleType;
    protected readonly ButtonMode = ButtonMode;
    protected readonly ERadioGroupType = ERadioGroupType;
    protected readonly ButtonActionType = ButtonActionType;

    public readonly selectedThemeColors = this.themeState.selectedColors;

    public get saveDisablePredicate(): boolean {
        return isEmptyButtonLabel(this.label?.value);
    }

    public setSize(value: string): void {
        this.size.setValue(value as ESize);
    }

    public customButtonChangeHandler(changes: CustomButtonEventPayload): void {
        this.buttonBase.setValue(changes.base);
        this.buttonHover.setValue(changes.hover);

        if (changes?.isIndependentCorners !== undefined) {
            this.isIndependentCorners.setValue(changes.isIndependentCorners);
        }
        if (changes?.radiusSettings) {
            this.advancedRadius.setValue(changes.radiusSettings);
        }
        if (changes?.advancedBorderSettings !== undefined) {
            this.advancedBorderSettings.setValue(changes.advancedBorderSettings);
        }
    }

    public get styleType(): ProductButtonFormType['styleType'] {
        return this.itemForm.get('styleType') as ProductButtonFormType['styleType'];
    }

    public get label(): ProductButtonFormType['label'] {
        return this.itemForm.get('label') as ProductButtonFormType['label'];
    }

    public get buttonBase(): ProductButtonFormType['buttonBase'] {
        return this.itemForm.get('buttonBase') as ProductButtonFormType['buttonBase'];
    }

    public get buttonHover(): ProductButtonFormType['buttonHover'] {
        return this.itemForm.get('buttonHover') as ProductButtonFormType['buttonHover'];
    }

    public get action(): ProductButtonFormType['action'] {
        return this.itemForm.get('action') as ProductButtonFormType['action'];
    }

    public get size(): ProductButtonFormType['size'] {
        return this.itemForm.get('size') as ProductButtonFormType['size'];
    }

    public get isIndependentCorners(): ProductButtonFormType['isIndependentCorners'] {
        return this.itemForm.get('isIndependentCorners') as ProductButtonFormType['isIndependentCorners'];
    }

    public get advancedRadius(): ProductButtonFormType['advancedRadius'] {
        return this.itemForm.get('advancedRadius') as ProductButtonFormType['advancedRadius'];
    }

    public get advancedBorderSettings(): ProductButtonFormType['advancedBorderSettings'] {
        return this.itemForm.get('advancedBorderSettings') as ProductButtonFormType['advancedBorderSettings'];
    }

    public setShadow(value: boolean): void {
        this.isShadow.setValue(value);
    }

    public setShadowSettings(settings: ShadowSettings): void {
        this.shadowSettings.setValue(settings);
    }

    public get isShadow(): ProductButtonFormType['isShadow'] {
        return this.itemForm.get('isShadow') as ProductButtonFormType['isShadow'];
    }

    public get shadowSettings(): ProductButtonFormType['shadowSettings'] {
        return this.itemForm.get('shadowSettings') as ProductButtonFormType['shadowSettings'];
    }

    public get shadowSettingsValue(): ButtonSettings['shadowSettings'] {
        return this.shadowSettings.value;
    }
}
